import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type TollFreeProfileNumber, type TollFreeProfile } from 'entities/Compliance/model'
import { openIntercom } from 'entities/Intercom'
import { type IResponseNumberVerifiedStatus } from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Attachment } from 'entities/Attachment/model/Attachment'
import type { PresentationModeStore } from 'widgets/PresentationMode'
import type { TollFreeStepsStore } from 'pages/settings/pages/compliance/pages/tollFree/store/tollFreeStepsStore'
import { DetailsModalContent } from 'pages/settings/pages/compliance/pages/tollFree/pages/UseCaseDetails/ui/DetailsModalContent/DetailsModalContent'

export class UseCaseDetailsStore {
  private _activeUseCase: TollFreeProfile | null = null
  private _activeUseCaseNumber: TollFreeProfileNumber | null = null
  private _activeTabKey = 'allNumbers'
  private _searchValue = ''
  private _modalId = nanoid()
  private _deniedType: IResponseNumberVerifiedStatus | null = null
  private _rejectedReason: string | null = null
  private _rejectedNumberId: number | null = null
  private _rejectedProfileId: number | null = null
  private _rejectedDesc =
    "Verify that all the business information fields are correct. Ensure that you have a live website URL. It's best to edit and resubmit within 7 days for a faster review."
  private _blockedDesc =
    'Ineligible for resubmission. If you feel that this was in error, please contact us. See forbidden message categories for more information on use cases and content.'

  constructor(
    private _tollFreeStepsStore: TollFreeStepsStore,
    private _entities: IEntitiesStore,
    private _presentationStore: PresentationModeStore
  ) {
    makeAutoObservable(this)
  }

  get activeUseCase() {
    return this._activeUseCase
  }

  get activeUseCaseNumber() {
    return this._activeUseCaseNumber
  }

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get activeTabNumbers() {
    let numbers: TollFreeProfileNumber[]

    switch (this._activeTabKey) {
      case 'internalReview':
        numbers = this.inPendingNumbers
        break
      case 'underReview':
        numbers = this.underReviewNumbers
        break
      case 'denied':
        numbers = this.rejectedNumbers
        break
      case 'blocked':
        numbers = this.blockedNumbers
        break
      case 'verified':
        numbers = this.completedNumbers
        break
      case 'allNumbers':
      default:
        numbers = this.allNumbers
        break
    }

    if (this._searchValue.trim()) {
      const search = this._searchValue.trim().toLowerCase()
      numbers = numbers.filter((number) => number.number?.toLowerCase().includes(search))
    }

    return numbers
  }

  get cardsItems() {
    return [
      {
        key: 'allNumbers',
        title: 'All numbers',
        amount: this.allNumbers.length,
      },
      {
        key: 'internalReview',
        title: 'Pending Review',
        amount: this.inPendingNumbers.length,
      },
      {
        key: 'underReview',
        title: 'In Carrier Review',
        amount: this.underReviewNumbers.length,
      },
      {
        key: 'denied',
        title: 'Rejected',
        amount: this.rejectedNumbers.length,
      },
      {
        key: 'blocked',
        title: 'Blocked',
        amount: this.blockedNumbers.length,
      },
      {
        key: 'verified',
        title: 'Approved',
        amount: this.completedNumbers.length,
      },
    ]
  }

  get allNumbers() {
    return this.activeUseCase?.numbers || []
  }

  get numberCount() {
    return this.allNumbers.length
  }

  get inPendingNumbers() {
    return this.allNumbers.filter((item) => item.isStatusPendingReview)
  }

  get underReviewNumbers() {
    return this.allNumbers.filter((item) => item.isStatusInCarrierReview)
  }

  get rejectedNumbers() {
    return this.allNumbers.filter((item) => item.isStatusRejected)
  }

  get blockedNumbers() {
    return this.allNumbers.filter((item) => item.isStatusBlocked)
  }

  get completedNumbers() {
    return this.allNumbers.filter((item) => item.isStatusVerified && this.activeUseCase?.completed)
  }

  get searchValue() {
    return this._searchValue
  }

  get deniedDesc() {
    if (this._deniedType === 'Denied') return this._rejectedDesc
    if (this._deniedType === 'Blocked') return this._blockedDesc

    return null
  }

  get rejectedReason() {
    return this._rejectedReason
  }

  get rejectedNumberId() {
    return this._rejectedNumberId
  }

  get rejectedProfileId() {
    return this._rejectedProfileId
  }

  get modalId() {
    return this._modalId
  }

  init = (id: number) => {
    const item = this._entities.complianceStore.organizationTollFree?.getItem(id)

    item && this.setActiveUseCase(item)
  }

  setActiveUseCase = (item: TollFreeProfile) => {
    this._activeUseCase = item
  }

  setActiveTabKey = (key: string) => {
    this._activeTabKey = key
  }

  setSearchValue = (value: string) => {
    this._searchValue = value.replace(/\D/g, '')
  }

  learnMoreModalAction = () => {
    modalStore.removeModal(this._modalId)
    openIntercom()
  }

  setActiveNumber = (item: TollFreeProfileNumber | null) => {
    this._deniedType = (item?.status as IResponseNumberVerifiedStatus) || null
    this._rejectedReason = item?.verificationErrorReason || null
    this._activeUseCaseNumber = item

    if (item?.isStatusRejected) {
      this._rejectedNumberId = item?.id || null
      this._rejectedProfileId = item?.individualTollFreeProfile?.id || null
    }
  }

  openDetailsModal = (item: TollFreeProfileNumber) => {
    const { complianceLayerStore, updateStatusAction } = this.tollFreeStepsStore

    this.setActiveNumber(item)

    this.tollFreeStepsStore.setUseCaseData()

    modalStore.addModal({
      id: this._modalId,
      width: 540,
      title: 'Toll-free number',
      showCloseButton: false,
      hiddenContent: true,
      ModalContent: () => (
        <DetailsModalContent
          errorMessage={{
            title: item.verificationErrorReason,
            desc: item.isStatusRejected ? this._rejectedDesc : this._blockedDesc,
          }}
          tollFreeNumber={item}
          withUpdata={item.isStatusRejected}
          complianceLayerStore={complianceLayerStore}
          learnMoreModalAction={this.learnMoreModalAction}
          updateStatusAction={() => updateStatusAction(this._activeUseCase?.id || 0, item.id)}
          openImageModal={this.openImageModal}
        />
      ),
    })
  }

  openImageModal = (url: string, name: string) => {
    this._presentationStore.setAttachments([new Attachment({ imageUrl: url, name })])
  }
}
